export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toBoolean(value) {
  return value === true || value === 1 || value === "1";
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 180) {
  const text = stripHtml(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function normalizeSearchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function parseNumber(value) {
  if (value === null || value === undefined || value === "") return null;

  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseLatLngPair(value) {
  if (!value) return null;

  const parts = String(value)
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) return null;

  const lat = parseNumber(parts[0]);
  const lng = parseNumber(parts[1]);

  if (lat === null || lng === null) return null;

  return [lat, lng];
}

export function parseLatLngSeparate(latValue, lngValue) {
  const lat = parseNumber(latValue);
  const lng = parseNumber(lngValue);

  if (lat === null || lng === null) return null;

  return [lat, lng];
}

export function normalizeExternalUrl(value = "") {
  const url = String(value || "").trim();
  if (!url) return "";

  if (/^https?:\/\//i.test(url)) return url;

  return `https://${url}`;
}

export function normalizePhoneHref(value = "") {
  const phone = String(value || "")
    .replace(/\s+/g, "")
    .replace(/[().-]/g, "");

  return phone ? `tel:${phone}` : "";
}

export function parseEmails(value = "") {
  return String(value || "")
    .split(/[;,]/)
    .map((mail) => mail.trim())
    .filter(Boolean);
}

export function buildMapsHref({ lat, lng, address }) {
  if (lat !== null && lng !== null) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }

  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  }

  return "";
}

export function normalizePointSearchIndex(point) {
  return normalizeSearchText(
    [
      point.title,
      point.typeLabel,
      point.category,
      point.description,
      point.address,
      point.phone,
      point.mail,
      point.website,
    ]
      .filter(Boolean)
      .join(" ")
  );
}

export function normalizeAssociationPoint(association) {
  const coords = parseLatLngPair(association?.gps_asso);
  if (!coords) return null;

  const [lat, lng] = coords;
  const address = association?.adresse_asso || "";

  const point = {
    id: `association-${association.id}`,
    rawId: association.id,
    type: "association",
    typeLabel: "Association",

    title: association?.nom_asso || "Association",
    category: association?.categorie || "",
    description: truncateText(association?.description_asso || "", 220),

    address,
    lat,
    lng,

    phone: association?.tel_asso || "",
    phoneHref: normalizePhoneHref(association?.tel_asso),

    mail: association?.mail_asso || "",
    emails: parseEmails(association?.mail_asso),

    website: normalizeExternalUrl(association?.website_asso),
    facebook: normalizeExternalUrl(association?.facebook_asso),
    instagram: "",

    mapsHref: buildMapsHref({ lat, lng, address }),
    original: association,
  };

  point.searchIndex = normalizePointSearchIndex(point);

  return point;
}

export function normalizeCommercePoint(commerce) {
  const coords = parseLatLngSeparate(commerce?.gps_lat, commerce?.gps_lng);
  if (!coords) return null;

  const [lat, lng] = coords;
  const address = [
    commerce?.adresse,
    [commerce?.code_postal, commerce?.ville].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const point = {
    id: `commerce-${commerce.id}`,
    rawId: commerce.id,
    type: "commerce",
    typeLabel: "Commerce",

    title: commerce?.nom_commerce || "Commerce",
    category: commerce?.categorie || "",
    description: truncateText(commerce?.description_commerce || "", 220),

    address,
    lat,
    lng,

    phone: commerce?.tel || "",
    phoneHref: normalizePhoneHref(commerce?.tel),

    mail: commerce?.mail || "",
    emails: parseEmails(commerce?.mail),

    website: normalizeExternalUrl(commerce?.website),
    facebook: normalizeExternalUrl(commerce?.facebook),
    instagram: normalizeExternalUrl(commerce?.instagram),

    mapsHref: buildMapsHref({ lat, lng, address }),
    original: commerce,
  };

  point.searchIndex = normalizePointSearchIndex(point);

  return point;
}

export function normalizeServicePoint(service) {
  const coords = parseLatLngPair(service?.gps_service);
  if (!coords) return null;

  const [lat, lng] = coords;
  const address = [
    service?.adresse_service,
    [service?.cp_service, service?.ville_service].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  const point = {
    id: `service-${service.id}`,
    rawId: service.id,
    type: "service",
    typeLabel: "Service municipal",

    title: service?.nom_service || "Service municipal",
    category: service?.categorie_service || service?.type_service || "",
    description: truncateText(
      service?.description_service || service?.accroche_service || "",
      220
    ),

    address,
    lat,
    lng,

    phone: service?.tel_service || service?.tel_secondaire || "",
    phoneHref: normalizePhoneHref(
      service?.tel_service || service?.tel_secondaire
    ),

    mail: service?.mail_service || "",
    emails: parseEmails(service?.mail_service),

    website: normalizeExternalUrl(service?.website_service),
    facebook: "",
    instagram: "",

    mapsHref: buildMapsHref({ lat, lng, address }),
    original: service,
  };

  point.searchIndex = normalizePointSearchIndex(point);

  return point;
}

export function sortPoints(points = []) {
  return [...points].sort((a, b) => {
    const typeOrder = {
      service: 1,
      commerce: 2,
      association: 3,
    };

    const orderA = typeOrder[a.type] || 99;
    const orderB = typeOrder[b.type] || 99;

    if (orderA !== orderB) return orderA - orderB;

    return normalizeSearchText(a.title).localeCompare(
      normalizeSearchText(b.title),
      "fr"
    );
  });
}